import api from './api';

export interface Fee {
  id?: number;
  user_id?: number;
  parent_id?: number | null;
  nation?: string;
  service?: string; // trademark|patent|barcode|company|copyright|other
  sub_service?: string;
  submit_channel?: string; // 递交渠道
  contact_info?: string;
  category_type?: string;
  fee_official?: number | string;
  package?: string;
  fee_inquiry?: number | string;
  fee_notarization?: number | string;
  fee_cost?: number | string;
  fee_profit?: number | string;
  fee_peer?: number | string;
  fee_service?: number | string;
  documents?: number[] | string; // 文档ID数组或JSON字符串
  documents_info?: Array<{
    id?: number;
    name?: string;
    file_name?: string;
    media_files_info?: Array<{
      id?: number;
      name?: string;
      file_name?: string;
      size?: number;
      file_size?: number;
      type?: string;
      file_type?: string;
      mime_type?: string;
      path?: string;
    }>;
  }>;
  dates_submit?: string;
  dates_complete?: string;
  status?: string;
  remark?: string;
  created_at?: string;
  updated_at?: string;
}

export interface FeeListParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  nation?: string;
  service?: string;
  sub_service?: string;
  submit_channel?: string;
  category_type?: string;
  sort_by?: string;
  sort_direction?: string;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface FeeFilterOptionsResponse {
  success: boolean;
  data: {
    nations: FilterOption[];
    services?: FilterOption[]; // 后端返回 services
    submit_channels?: FilterOption[];
    category_types?: FilterOption[];
    statuses?: FilterOption[];
  };
}

export interface FeeListResponse {
  success: boolean;
  data: {
    data: Fee[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export interface FeeResponse {
  success: boolean;
  message?: string;
  data?: Fee;
  errors?: any;
}

class FeeService {
  // 获取费用记录列表
  async getFees(params?: FeeListParams): Promise<FeeListResponse> {
    const response = await api.get('/fees', { params });
    return response.data;
  }

  // 获取筛选选项
  async getFilterOptions(): Promise<FeeFilterOptionsResponse> {
    const response = await api.get('/fees/filter-options');
    return response.data;
  }

  // 创建费用记录
  async createFee(feeData: Fee): Promise<FeeResponse> {
    const response = await api.post('/fees', feeData);
    return response.data;
  }

  // 获取费用记录详情
  async getFee(id: number): Promise<FeeResponse> {
    const response = await api.get(`/fees/${id}`);
    return response.data;
  }

  // 更新费用记录
  async updateFee(id: number, feeData: Fee): Promise<FeeResponse> {
    const response = await api.put(`/fees/${id}`, feeData);
    return response.data;
  }

  // 删除费用记录
  async deleteFee(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/fees/${id}`);
    return response.data;
  }

  // 批量删除费用记录
  async batchDeleteFees(ids: number[]): Promise<{ success: boolean; message: string; data?: { deleted_count: number } }> {
    const response = await api.post('/fees/batch-delete', { ids });
    return response.data;
  }

  // 导出费用记录数据
  async exportFees(params?: FeeListParams): Promise<Blob> {
    const response = await api.get('/fees-export', {
      params,
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      }
    });
    return response.data;
  }

  // 导入费用记录数据
  async importFees(file: File): Promise<{ success: boolean; message: string; data?: any }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/fees-import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  // 获取指定国家/地区的递交渠道选项（来源于 fees.submit_channel 去重）
  async getSubmitChannelsByNation(nation?: string): Promise<{ success: boolean; data: { submit_channels: FilterOption[] } }> {
    const response = await api.get('/fees/submit-channels', { params: { nation } });
    return response.data;
  }
}

const feeService = new FeeService();
export default feeService;
